import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import apiService from '@/services/api'
import { formatCarrierEventTitle, formatDcsaEventLabel } from '@/utils/dcsaEventLabels'

interface LotTraceabilityModalProps {
  isOpen: boolean
  onClose: () => void
  rowId?: string
  data?: {
    product: string
    productDescription: string
    lot: string
    bags: number
    bagsCapacity: number
    bagsUnit: string
    location: string
    status: string
    lastUpdated: string
  }
}

type TraceabilityResponse = {
  row: {
    id: string
    buyContractNumber: string | null
    saleContractNumber: string | null
    bookingNumber: string | null
    vesselVoyage: string | null
    masterShippingNumber: string | null
    containerNumber: string | null
    batchNumber: string | null
    product: string | null
    description: string | null
    customerReference: string | null
    barcode: string | null
    pieces: number | null
    bags: number | null
    weight: number | null
    volume: number | null
    itemNumber: string | null
    dispatchNumber: string | null
    pickingListNumber: string | null
    dispatchDate: string | null
    location: string | null
    status: string | null
    updatedAt: string | null
  }
  shipment: {
    id: string
    containerNumber: string | null
    booking: string | null
    buyContract: string | null
    saleContract: string | null
    vessel: string | null
    bl: string | null
    carrier: string | null
    pol: string | null
    pod: string | null
    eta: string | null
    etd: string | null
    status: string | null
    customer: string | null
    product: string | null
    incoterm: string | null
    factory: string | null
    forwarder: string | null
    createdAt: string | null
    updatedAt: string | null
  } | null
  clearing: {
    id: string
    status: string
    port: string | null
    clearingAgent: string | null
    updatedAt: string | null
  } | null
  dispatches: Array<{
    id: string
    lotNumber: string | null
    destination: string | null
    customer: string | null
    dispatchDate: string | null
    status: string | null
    qtyDispatched: number | null
    qtyUnit: string | null
    notes: string | null
    updatedAt: string | null
  }>
  timeline: Array<{
    at: string
    title: string
    status: string
    source: string
  }>
}

const fmtDateTime = (value?: string | null): string => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hh}:${mm}`
}

const LotTraceabilityModal = ({ isOpen, onClose, rowId, data }: LotTraceabilityModalProps) => {
  const [trace, setTrace] = useState<TraceabilityResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return undefined

    const { body, documentElement } = document
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverflow = documentElement.style.overflow

    body.style.overflow = 'hidden'
    documentElement.style.overflow = 'hidden'

    return () => {
      body.style.overflow = previousBodyOverflow
      documentElement.style.overflow = previousHtmlOverflow
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !rowId) return
    let active = true
    const run = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await apiService.get<{ success: boolean; data: TraceabilityResponse }>(
          `/inventory/stock-recon/${rowId}/traceability`
        )
        if (!active) return
        setTrace((res as any)?.data ?? res)
      } catch (err: unknown) {
        if (!active) return
        setError(err instanceof Error ? err.message : 'Failed to load traceability details')
        setTrace(null)
      } finally {
        if (active) setLoading(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [isOpen, rowId])

  const progress = useMemo(() => {
    if (!data) return 0
    const capacity = data.bagsCapacity > 0 ? data.bagsCapacity : data.bags
    if (!capacity || capacity <= 0) return 0
    return Math.min(100, Math.round((data.bags / capacity) * 100))
  }, [data?.bags, data?.bagsCapacity])

  const timelineItems = useMemo(
    () =>
      (trace?.timeline ?? []).map((item) =>
        item.source === 'carrier'
          ? {
              ...item,
              title: formatCarrierEventTitle(item.title, item.status),
              status: formatDcsaEventLabel(item.status),
            }
          : item
      ),
    [trace?.timeline]
  )

  if (!isOpen || !data) return null

  const detailRow = trace?.row
  const shipment = trace?.shipment

  const modalContent = (
    <div
      className="fixed bg-white left-0 right-0 bottom-0 top-[92px] lg:left-[242px]"
      style={{ zIndex: 60 }}
    >
      <div
        className="flex flex-col"
        style={{
          fontFamily: 'Lexend Deca',
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        <div
          className="flex items-center justify-between px-6"
          style={{
            width: '100%',
            height: '64px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex items-center justify-center hover:opacity-80 transition-opacity"
              style={{ width: '28px', height: '28px' }}
              aria-label="Back"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.25 3.75L6.75 9L11.25 14.25"
                  stroke="#69697A"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h2
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '32px',
                color: '#212126'
              }}
            >
              Lot traceability &amp; History
            </h2>
          </div>
          <span
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '20px',
              color: '#69697A',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}>&gt;</span>
            <span style={{ color: '#69697A' }}>Storage &amp; Inventory</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}>&gt;</span>
            <span style={{ color: '#212126' }}>Lot traceability</span>
          </span>
        </div>

        <div
          className="flex-1 overflow-y-auto p-6"
          style={{ backgroundColor: '#FAFAFA', overscrollBehavior: 'contain' }}
        >
          <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#212126'
                    }}
                  >
                    Track Your Shipment
                  </h3>
                  <p
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 300,
                      fontSize: '12px',
                      color: '#69697A'
                    }}
                  >
                    Enter your tracking number to get real-time updates on your shipment
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  value={detailRow?.batchNumber ?? data.lot}
                  readOnly
                  className="flex-1 border rounded-lg"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: '12px',
                    height: '36px',
                    padding: '0 12px',
                    border: '1px solid #E9E9EA'
                  }}
                />
                <button
                  className="text-white rounded-md opacity-70"
                  disabled
                  style={{
                    height: '36px',
                    padding: '0 14px',
                    backgroundColor: '#212126',
                    fontFamily: 'Lexend Deca',
                    fontSize: '12px'
                  }}
                >
                  Tracking Active
                </button>
              </div>
              <div className="mt-2">
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontSize: '11px',
                    color: '#69697A'
                  }}
                >
                  Recent Tracking Numbers
                </span>
                <div
                  className="mt-1 inline-flex items-center rounded-md border"
                  style={{
                    borderColor: '#E9E9EA',
                    padding: '2px 6px',
                    fontSize: '11px',
                    color: '#212126'
                  }}
                >
                  {detailRow?.batchNumber ?? data.lot}
                </div>
              </div>
            <div
              className="mt-6 rounded-lg border p-4"
              style={{ backgroundColor: '#F8F9FB', borderColor: '#E9E9EA' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div
                    className="w-9 h-9 rounded flex items-center justify-center"
                    style={{ backgroundColor: '#FEF4F3' }}
                  >
                    <img src="/images/box.svg" alt="Product" style={{ width: '18px', height: '18px' }} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontSize: '12px',
                        color: '#212126'
                      }}
                    >
                      {data.product}
                    </div>
                    <div
                      className="mt-1"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 300,
                        fontSize: '11px',
                        color: '#69697A'
                      }}
                    >
                      Previous Lot IDs: {detailRow?.batchNumber ?? data.lot}
                    </div>
                  </div>
                </div>
                <div
                  className="grid grid-cols-2 gap-x-6 gap-y-1"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '11px' }}
                >
                  <span style={{ color: '#69697A' }}>From</span>
                  <span style={{ color: '#69697A' }}>To</span>
                  <span style={{ color: '#212126' }}>{shipment?.pol || '—'}</span>
                  <span style={{ color: '#212126' }}>{shipment?.pod || '—'}</span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-6 gap-3">
                {[
                  { label: 'Status', value: detailRow?.status ?? data.status },
                  { label: 'Dispatch Date', value: fmtDateTime(detailRow?.dispatchDate) },
                  { label: 'Dispatch No', value: detailRow?.dispatchNumber ?? '—' },
                  { label: 'Received Date', value: fmtDateTime(detailRow?.dispatchDate) },
                  { label: 'Location', value: detailRow?.location ?? data.location ?? '—' },
                  { label: 'Last Updated', value: fmtDateTime(detailRow?.updatedAt ?? data.lastUpdated) }
                ].map((item) => (
                  <div key={item.label}>
                    <div style={{ fontSize: '10px', color: '#69697A' }}>{item.label}</div>
                    <div style={{ fontSize: '11px', color: '#212126' }}>{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <div style={{ fontSize: '10px', color: '#69697A' }}>Shipment Progress</div>
                <div className="mt-1 flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full"
                      style={{
                        width: `${progress}%`,
                        backgroundColor: '#2C71F6'
                      }}
                    />
                  </div>
                  <div style={{ fontSize: '11px', color: '#69697A' }}>{progress}% Complete</div>
                </div>
                <div style={{ fontSize: '10px', color: '#69697A', marginTop: '6px' }}>
                  Shipped 10 Apr 2025
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 items-start">
              <div
                className="col-span-1 rounded-lg border p-4 self-start"
                style={{ backgroundColor: '#FFFFFF', borderColor: '#E9E9EA' }}
              >
                <div
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '12px',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  Shipment Timeline
                </div>
                <div className="space-y-4">
                  {(loading ? [] : timelineItems).map((item, index) => {
                    const isActive = index >= Math.max(0, timelineItems.length - 3)
                    const indicatorColor = isActive ? '#2C71F6' : '#C7CCD5'
                    const textColor = '#212126'
                    const isLast = index === timelineItems.length - 1
                    const at = new Date(item.at)
                    const date = Number.isNaN(at.getTime()) ? '—' : at.toLocaleDateString('en-GB')
                    const time = Number.isNaN(at.getTime()) ? '—' : at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

                    return (
                      <div key={`${item.at}-${item.title}-${index}`} className="flex gap-3">
                        <div
                          className="text-right flex-shrink-0"
                          style={{ width: '56px', color: '#69697A', fontSize: '10px', lineHeight: '14px' }}
                        >
                          <div>{date}</div>
                          <div>{time}</div>
                        </div>
                        <div className="relative flex flex-col items-center" style={{ width: '18px' }}>
                          <div
                            className="absolute"
                            style={{
                              top: 0,
                              bottom: isLast ? '10px' : 0,
                              width: '1px',
                              backgroundColor: '#E5E7EB'
                            }}
                          />
                          <div
                            className="relative z-10 flex items-center justify-center rounded-full"
                            style={{
                              width: '18px',
                              height: '18px',
                              backgroundColor: '#FFFFFF',
                              border: `1px solid ${indicatorColor}`
                            }}
                          >
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <polyline
                                points="2.5,6.5 5,9 9.5,3.5"
                                stroke={indicatorColor}
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="pb-4 flex-1">
                          <div
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 500,
                              fontSize: '11px',
                              lineHeight: '16px',
                              color: textColor
                            }}
                          >
                            {item.title}
                          </div>
                          <div
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 400,
                              fontSize: '10px',
                              lineHeight: '14px',
                              color: textColor
                            }}
                          >
                            {item.status}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {!loading && timelineItems.length === 0 && (
                    <div style={{ fontFamily: 'Lexend Deca', fontSize: '11px', color: '#69697A' }}>
                      No timeline events available for this lot yet.
                    </div>
                  )}
                </div>
              </div>
              <div
                className="col-span-2 rounded-lg border p-4 self-start"
                style={{ backgroundColor: '#FFFFFF', borderColor: '#E9E9EA' }}
              >
                <div
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '12px',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  Shipment Details
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#E9E9EA',
                    margin: '8px 0'
                  }}
                />
                <div
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  FCLStockRecon Details
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Tracking Number', value: shipment?.id ?? '—' },
                    { label: 'Vessel', value: shipment?.vessel ?? detailRow?.vesselVoyage ?? '—' },
                    { label: 'Container', value: shipment?.containerNumber ?? detailRow?.containerNumber ?? '—' },
                    { label: 'Booking No', value: shipment?.booking ?? detailRow?.bookingNumber ?? '—' },
                    { label: 'Master Shipping No', value: shipment?.bl ?? detailRow?.masterShippingNumber ?? '—' },
                    { label: 'Product', value: shipment?.product ?? detailRow?.product ?? data.product },
                    { label: 'Picking List No', value: detailRow?.pickingListNumber ?? '—' },
                    { label: 'Product Description', value: detailRow?.description ?? data.productDescription ?? '—' },
                    { label: '', value: '' }
                  ].map((item, index) => (
                    <div key={`${item.label}-${index}`}>
                      {item.label && (
                        <div style={{ fontSize: '10px', color: '#69697A' }}>{item.label}</div>
                      )}
                      {item.value && (
                        <div style={{ fontSize: '11px', color: '#212126' }}>{item.value}</div>
                      )}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#E9E9EA',
                    margin: '12px 0'
                  }}
                />
                <div
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  Customer Information
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Customer Reference', value: detailRow?.customerReference ?? '—' },
                    { label: 'Customer', value: shipment?.customer ?? '—' },
                    { label: 'Forwarder', value: shipment?.forwarder ?? '—' },
                    { label: 'Barcode', value: detailRow?.barcode ?? '—' },
                    { label: '', value: '' },
                    { label: '', value: '' }
                  ].map((item, index) => (
                    <div key={`${item.label}-${index}`}>
                      {item.label && (
                        <div style={{ fontSize: '10px', color: '#69697A' }}>{item.label}</div>
                      )}
                      {item.value && (
                        <div style={{ fontSize: '11px', color: '#212126' }}>{item.value}</div>
                      )}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#E9E9EA',
                    margin: '12px 0'
                  }}
                />
                <div
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  Lot Details
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div style={{ fontSize: '10px', color: '#69697A' }}>Bags</div>
                    <div style={{ fontSize: '11px', color: '#212126' }}>{detailRow?.bags ?? data.bags} / {data.bagsCapacity} {data.bagsUnit || 'FCL'}</div>
                    <div className="mt-1 h-1.5 rounded-full" style={{ backgroundColor: '#FEE2E2' }}>
                      <div className="h-1.5 rounded-full" style={{ width: '60%', backgroundColor: '#E93323' }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: '#69697A' }}>Weight</div>
                    <div style={{ fontSize: '11px', color: '#212126' }}>{detailRow?.weight != null ? `${detailRow.weight} Kg` : '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: '#69697A' }}>Volume</div>
                    <div style={{ fontSize: '11px', color: '#212126' }}>{detailRow?.volume != null ? `${detailRow.volume} M³` : '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: '#69697A' }}>Item Number</div>
                    <div style={{ fontSize: '11px', color: '#212126' }}>{detailRow?.itemNumber ?? '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: '#69697A' }}>Hazardous</div>
                    <div style={{ fontSize: '11px', color: '#212126' }}>—</div>
                  </div>
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#E9E9EA',
                    margin: '12px 0'
                  }}
                />
                <div
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  Dispatch History
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {(trace?.dispatches ?? []).slice(0, 5).map((d) => (
                    <div key={d.id} style={{ border: '1px solid #E9E9EA', borderRadius: '6px', padding: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#212126', fontFamily: 'Lexend Deca' }}>
                        {d.destination || 'Unknown destination'} • {d.status || '—'}
                      </div>
                      <div style={{ fontSize: '10px', color: '#69697A', fontFamily: 'Lexend Deca' }}>
                        {fmtDateTime(d.dispatchDate || d.updatedAt)}{d.qtyDispatched != null ? ` • ${d.qtyDispatched} ${d.qtyUnit || ''}` : ''}
                      </div>
                    </div>
                  ))}
                  {!loading && (trace?.dispatches?.length ?? 0) === 0 && (
                    <div style={{ fontSize: '11px', color: '#69697A', fontFamily: 'Lexend Deca' }}>
                      No dispatch history found for this lot.
                    </div>
                  )}
                </div>
              </div>
            </div>
            {loading && (
              <div style={{ marginTop: '12px', fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                Loading traceability details...
              </div>
            )}
            {error && (
              <div style={{ marginTop: '12px', fontFamily: 'Lexend Deca', fontSize: '12px', color: '#E93323' }}>
                {error}
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
  )

  return createPortal(modalContent, document.body)
}

export default LotTraceabilityModal
